import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconBuilding, IconMail, IconMapPin, IconPhone } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import { ClientsService } from '@/services/clients'
import * as z from 'zod'

// Esquema propi de les empreses
const companySchema = z.object({
  // Dades de l'empresa
  empresa: z.string().min(1, "El nom de l'empresa és obligatori"),
  nif_empresa: z.string().regex(/^[A-Z]\d{8}$/, 'El CIF ha de tenir el format B12345678'),
  
  // Persona de contacte
  nom: z.string().min(1, 'El nom del contacte és obligatori'),
  cognoms: z.string().optional(),
  email: z.string().email('Correu electrònic no vàlid'),
  telefon: z.string().optional(),
  
  // Adreça fiscal
  adreca: z.string().min(1, "L'adreça és obligatòria"),
  ciutat: z.string().min(1, 'La ciutat és obligatòria'),
  codi_postal: z.string().regex(/^\d{5}$/, 'El codi postal ha de tenir 5 xifres'),
  provincia: z.string().optional(),
  
  // Camps addicionals
  notes: z.string().optional(),
})

type CompanyFormValues = z.infer<typeof companySchema>

interface CreateCompanyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function CreateCompanyModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateCompanyModalProps) {
  const [loading, setLoading] = useState(false)
  const { success, error } = useToast()

  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
    // Sempre en blanc: el formulari venia pre-emplenat amb una empresa real fixada al codi
    // i el que s'escrivia s'hi enganxava al darrere (§11 de `plans/miranda-grups-i-torns.md`).
    defaultValues: {
      empresa: '',
      nif_empresa: '',
      nom: '',
      cognoms: '',
      email: '',
      telefon: '',
      adreca: '',
      ciutat: '',
      codi_postal: '',
      provincia: '',
      notes: '',
    },
  })

  const onSubmit = async (data: CompanyFormValues) => {
    setLoading(true)

    try {
      // Dades per al backend
      const createData = {
        // Marcar como empresa
        tipus_client: 'empresa',
        
        // Dades de l'empresa
        empresa: data.empresa,
        nif_empresa: data.nif_empresa.toUpperCase(),
        
        // Dades del contacte
        nom: data.nom,
        cognoms: data.cognoms || '',
        email: data.email,
        telefon: data.telefon,
        
        // Document de l'empresa
        tipus_document: 'CIF',
        num_document: data.nif_empresa.toUpperCase(),
        
        // Adreça fiscal
        adreca: data.adreca,
        ciutat: data.ciutat,
        codi_postal: data.codi_postal,
        provincia: data.provincia || data.ciutat,
        pais: 'ES',
        
        // Notes
        notes: data.notes,
        
        // Flags
        actiu: true,
        es_vip: false,
        consentiment_marketing: false,
      }

      // El servei no llança en errors HTTP: torna `{ success: false, message }`
      const result = await ClientsService.create(createData)
      if (!result.success) throw new Error(result.message || 'Error en crear el client')

      // `useToast` vol `(missatge, descripció)`, no un objecte: amb l'objecte, Sonner
      // el passava com a fill de React, l'arbre petava i la pàgina de clients es quedava
      // en blanc just després del POST 201 (§11 de `plans/miranda-grups-i-torns.md`).
      success('Empresa creada', `${data.empresa} s'ha creat correctament`)


      form.reset()
      onOpenChange(false)
      onSuccess()
    } catch (err: any) {
      console.error('Error en crear empresa:', err)
      error(
        "Error en crear l'empresa",
        err.response?.data?.message || err.message || 'Error desconegut',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconBuilding className="w-5 h-5" />
            Crear empresa client
          </DialogTitle>
          <DialogDescription>
            Afegeix una empresa per facturar-hi. Els camps marcats amb * són obligatoris.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Dades de l'empresa */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Dades de l'empresa</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="empresa"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Raó social *</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="EXEMPLE SERVEIS SL"
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="nif_empresa"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CIF *</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="B12345678"
                          disabled={loading}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase()
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Persona de contacte */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Persona de contacte</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="nom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom *</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="Joan"
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="cognoms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cognoms</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="Moon River"
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <IconMail className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input 
                            {...field} 
                            className="pl-8"
                            type="email"
                            placeholder="contacte@exemple.com"
                            disabled={loading}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="telefon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telèfon</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <IconPhone className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input 
                            {...field} 
                            className="pl-8"
                            placeholder="93 000 00 00"
                            disabled={loading}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Adreça fiscal */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <IconMapPin className="w-4 h-4" />
                Adreça fiscal
              </h3>
              
              <FormField
                control={form.control}
                name="adreca"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adreça *</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Carrer Exemple, 1, 2n 1a"
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="ciutat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ciutat *</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="Barcelona"
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="provincia"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Província</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="Barcelona"
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="codi_postal"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>C.P. *</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          placeholder="08000"
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes internes</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Informació addicional sobre l'empresa…"
                      rows={3}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel·la
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creant…' : "Crea l'empresa"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
